/**
 * useCatalog — загружает прайс-каталог GET /api/price-catalog.
 * В DEV/mock-режиме возвращает встроенный каталог без сетевого запроса.
 */

import { useQuery } from '@tanstack/react-query'
import { catalogApi } from '@/api/client'
import type { PriceCatalog } from '@/types'

const USE_MOCK = import.meta.env.VITE_USE_MOCK != null
  ? import.meta.env.VITE_USE_MOCK === 'true'
  : import.meta.env.DEV

const MOCK_CATALOG: PriceCatalog = {
  base: {
    id: 'base',
    name: 'Базовый пакет (1 канал)',
    cost: 60000,
    base_channel: 'telegram',
    includes: ['Диалоговый ИИ', 'Запись клиентов', 'Консультация 24/7'],
  },
  channels: [
    { id: 'telegram', name: 'Telegram', one_time: 0, enters_contract_50_50: true, base_channel: true },
    { id: 'vk_max', name: 'VK Max', one_time: 20000, enters_contract_50_50: true },
    { id: 'avito', name: 'Авито', one_time: 20000, enters_contract_50_50: true },
    { id: 'widget', name: 'Сайт-виджет', one_time: 20000, enters_contract_50_50: true },
  ],
  integrations: [
    { id: 'bitrix24', name: 'Битрикс24', one_time: 15000, category: 'CRM' },
    { id: 'amocrm', name: 'amoCRM', one_time: 15000, category: 'CRM' },
    { id: 'yclients', name: 'YCLIENTS', one_time: 12000, category: 'Запись' },
    { id: 'google_sheets', name: 'Google Таблицы', one_time: 8000, category: 'Отчёты' },
    { id: 'moysklad', name: 'МойСклад', one_time: 12000, category: 'Учёт' },
  ],
  modules: [
    { id: 'analytics', name: 'Расширенная аналитика', one_time: 10000, monthly: 1500 },
    { id: 'voice', name: 'Голосовые сообщения', one_time: 8000, monthly: 0 },
    { id: 'multibot', name: 'Мульти-бот (несколько агентов)', one_time: 20000, monthly: 2000 },
    { id: 'upsell_ai', name: 'Апсейл-рекомендации', one_time: 12000, monthly: 1000 },
  ],
  subscription: {
    monthly_base: 4900,
    included_dialogs: 100,
    brackets: [
      { from: 101, to: 300, rate: 35, label: '101–300 диалогов' },
      { from: 301, to: 600, rate: 28, label: '301–600 диалогов' },
      { from: 601, to: null, rate: 22, label: '601+ диалогов' },
    ],
  },
}

export const catalogKeys = {
  all: ['catalog'] as const,
}

export function useCatalog() {
  return useQuery({
    queryKey: catalogKeys.all,
    queryFn: async (): Promise<PriceCatalog> => {
      if (USE_MOCK) return MOCK_CATALOG
      return catalogApi.get()
    },
    staleTime: 10 * 60 * 1000, // 10 мин — каталог меняется редко
  })
}
